<template>
  <!-- Werkzeugleiste beim Schreiben – EINE Komponente für „Neue Nachricht“,
       das Antwortfeld der Korrespondenz und das Unterhaltungs-Fenster
       (Kundenfeedback 25.10.2026 Teil 3, I: „Es wäre praktisch, wenn einfach
       immer alles gleich abläuft, egal ob ich eine neue E-Mail erstelle,
       antworte, weiterleite …“). Änderungen hier gelten überall. -->
  <div class="ct" data-testid="compose-tools">
    <div class="ct__actions">
      <button
        class="ct__btn"
        data-testid="compose-insert-template"
        :title="$gt('Vorlage aus den E-Mail-Vorlagen einfügen (Unternehmens- oder eigene Vorlagen)')"
        type="button"
        @click="openTemplatePicker"
      >
        <FileText :size="15" />
        <span class="ct__label">{{ $gt('Vorlage einfügen') }}</span>
      </button>
      <button
        class="ct__btn"
        data-testid="compose-save-template"
        :disabled="!canSaveTemplate"
        :title="$gt('Aktuellen Text (ohne Signatur und zitierten Verlauf) als Vorlage speichern')"
        type="button"
        @click="saveAsTemplate"
      >
        <BookmarkPlus :size="15" />
        <span class="ct__label">{{ $gt('Als Vorlage speichern') }}</span>
      </button>
      <button
        class="ct__btn"
        data-testid="compose-insert-snippet"
        :title="$gt('Textbaustein an der Cursorposition einfügen')"
        type="button"
        @click="openSnippets"
      >
        <TextQuote :size="15" />
        <span class="ct__label">{{ $gt('Textbaustein') }}</span>
      </button>
      <!-- Standort für die Signatur: nur bei mehreren zugeordneten Standorten. -->
      <label
        v-if="locations.length > 1"
        class="ct__btn ct__btn--select"
        data-testid="compose-signature-location"
        :title="$gt('Standort für die Signatur wählen')"
      >
        <MapPin :size="15" />
        <select
          :aria-label="$gt('Standort für die Signatur')"
          class="ct__select"
          :value="locationId ?? ''"
          @change="onLocationChange(Number(($event.target as HTMLSelectElement).value))"
        >
          <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ signature.locationLabel(loc) }}</option>
        </select>
      </label>
      <button
        :aria-pressed="signatureShown ? 'true' : 'false'"
        class="ct__btn"
        :class="{ 'ct__btn--on': signatureShown }"
        data-testid="compose-toggle-signature"
        :disabled="signatureBusy"
        :title="signatureShown ? $gt('Signatur aus dem Text entfernen') : $gt('Signatur in den Text einfügen')"
        type="button"
        @click="toggleSignature"
      >
        <PenLine :size="15" />
        <span class="ct__label">{{ $gt('Signatur') }}</span>
      </button>
      <button
        class="ct__btn"
        data-testid="compose-edit-signature"
        :title="$gt('Ihre Signatur hier bearbeiten – der Text bleibt erhalten')"
        type="button"
        @click="editSignature"
      >
        <UserPen :size="15" />
        <span class="ct__label">{{ $gt('Signatur bearbeiten') }}</span>
      </button>
      <button
        class="ct__btn"
        data-testid="compose-attach-from-case"
        :title="$gt('Dokumente oder frühere E-Mails (als PDF) aus der Akte anhängen')"
        type="button"
        @click="pickerOpen = true"
      >
        <FolderOpen :size="15" />
        <span class="ct__label">{{ $gt('Aus Fallakte') }}</span>
      </button>
      <label class="ct__btn" data-testid="compose-attach-from-pc" :title="$gt('Datei von Ihrem PC anhängen')">
        <Paperclip :size="15" />
        <span class="ct__label">{{ $gt('Anhang vom PC') }}</span>
        <input hidden multiple type="file" @change="onFilePick">
      </label>
    </div>
    <div class="ct__end">
      <slot name="end" />
    </div>

    <MailTemplatePicker
      v-model="pickerVisible"
      :case-type="caseType"
      :categories="templates.categories.value"
      :loading="templates.loading.value"
      :suggested-category="suggestedCategory"
      :templates="templates.list.value"
      :values="templateValues"
      @manage="manageTemplates"
      @select="applyTemplate"
    />

    <MailTemplateEditor
      v-model="editorVisible"
      :can-manage-shared="templates.canManageShared.value"
      :categories="templates.categories.value"
      :companies="templates.companies.value"
      :default-company-id="signature.signatureLocation()?.idCompany ?? null"
      :prefill="editorPrefill"
      :preview-values="templateValues"
      :template="null"
      @saved="templates.remember"
    />

    <!-- Vorlagenverwaltung im Fenster – für Rollen ohne Zugang zu den
         Kommunikationseinstellungen (eigene Vorlagen pflegt jeder selbst). -->
    <v-dialog
      v-model="managerVisible"
      max-width="1140"
      scrollable
      width="1140"
      @after-leave="templates.load(true)"
    >
      <v-card class="ct__manager" data-testid="template-manager-dialog" rounded="lg">
        <header class="ct__manager-head">
          <h3 class="ct__manager-title">{{ $gt('E-Mail-Vorlagen') }}</h3>
          <ButtonBase color="grey" icon-style icon-type="cancel" @click="managerVisible = false" />
        </header>
        <v-divider />
        <div class="ct__manager-body">
          <MailTemplatesManager
            v-if="managerVisible"
            :create-new="!!managerLink?.create"
            :initial-category="managerLink?.category ?? ''"
            :initial-owner="managerLink?.owner ?? null"
          />
        </div>
      </v-card>
    </v-dialog>

    <TextSnippetDialog ref="snippetDialog" @insert="insertSnippet" />

    <SignatureEditDialog
      v-model="signatureEditorVisible"
      :location="signature.signatureLocation()"
      @saved="onSignatureEdited"
    />

    <CaseAttachmentPickerDialog
      v-model="pickerOpen"
      :cases="cases"
      :initial-case="pickerCase"
      @attach="emit('attach-case', $event)"
    />
  </div>
</template>

<script setup lang="ts">
  import type { AttachPayload, PickerCase } from './CaseAttachmentPickerDialog.vue'
  import type { ComposeSignature } from './useComposeSignature'
  import type { MailTemplatePrefill } from '@/components/messages/templates/MailTemplateEditor.vue'
  import type { MailTemplate } from '@/services/mailTemplatesApi'
  import type { MailTemplateValues } from '@/utils/mailTemplate'
  import type { TemplateManagerLink } from '@/utils/mailTemplateFilter'
  import { BookmarkPlus, FileText, FolderOpen, MapPin, Paperclip, PenLine, TextQuote, UserPen } from 'lucide-vue-next'
  import { computed, defineAsyncComponent, nextTick, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import ButtonBase from '@/components/buttons/ButtonBase.vue'
  import MailTemplateEditor from '@/components/messages/templates/MailTemplateEditor.vue'
  import MailTemplatePicker from '@/components/messages/templates/MailTemplatePicker.vue'
  import TextSnippetDialog from '@/components/messages/TextSnippetDialog.vue'
  import { gt } from '@/i18n/gt'
  import { invalidateCompanySignature } from '@/services/emailSignature'
  import { useAuthStore } from '@/stores/authStore'
  import { useSnackbarStore } from '@/stores/snackbarStore'
  import { templateManagerQuery } from '@/utils/mailTemplateFilter'
  import CaseAttachmentPickerDialog from './CaseAttachmentPickerDialog.vue'
  import { bodyForTemplate, hasSignature, insertTemplate, plainText, removeSignature, setSignature } from './composeBody'
  import { type RenderedTemplate, renderTemplateForInsert } from './composeTemplate'
  import SignatureEditDialog from './SignatureEditDialog.vue'
  import { useMailTemplates } from './useMailTemplates'

  const MailTemplatesManager = defineAsyncComponent(() => import('@/pages/pageCommunicationSettings/MailTemplatesManager.vue'))

  /** Was die Werkzeugleiste vom Editor (MessageComposer) braucht. */
  export interface ComposerHandle {
    editorEl: () => HTMLElement | null
    syncFromEditor: () => void
    saveSelection: () => void
    insertHtml: (html: string) => void
  }

  const props = withDefaults(defineProps<{
    /** Liefert den MessageComposer, in dessen Text eingefügt wird. */
    composer: () => ComposerHandle | null | undefined
    /** Aktueller Text (v-model des Composers) – für die Knopf-Zustände. */
    html?: string
    signature: ComposeSignature
    /** Platzhalter-Werte (Nutzer, Standort, Akte) für Vorlagen. */
    templateValues: MailTemplateValues
    /** Vorauswahl „An wen?“ im Vorlagen-Fenster. */
    suggestCategory?: (() => string) | null
    /** Vor dem Öffnen des Vorlagen-Fensters (z. B. Falldaten nachladen). */
    prepare?: (() => Promise<void>) | null
    /** Betreff als Vorbelegung für „Als Vorlage speichern“. */
    subject?: string
    pickerCase?: PickerCase | null
    /** Fallart der Akte – Kasko-Akten zeigen die Kasko-Schreiben zuerst. */
    caseType?: string | null
    cases?: PickerCase[] | null
    /** Vor dem Wechsel in die Vorlagenverwaltung (Entwurf sichern, Fenster minimieren). */
    beforeLeave?: (() => void) | null
  }>(), {
    html: '',
    suggestCategory: null,
    prepare: null,
    subject: '',
    pickerCase: null,
    caseType: '',
    cases: null,
    beforeLeave: null,
  })

  const emit = defineEmits<{
    'update:signatureOn': [on: boolean]
    /** Text wurde von der Werkzeugleiste geändert (Vorlage, Signatur). */
    'changed': []
    'attach-files': [files: File[]]
    'attach-case': [payload: AttachPayload]
    'template-inserted': [template: RenderedTemplate]
  }>()

  const router = useRouter()
  const snackbar = useSnackbarStore()
  const templates = useMailTemplates()

  const pickerVisible = ref(false)
  const editorVisible = ref(false)
  const managerVisible = ref(false)
  const managerLink = ref<TemplateManagerLink | null>(null)
  const editorPrefill = ref<MailTemplatePrefill | null>(null)
  const suggestedCategory = ref('')
  const signatureEditorVisible = ref(false)
  const signatureBusy = ref(false)
  const pickerOpen = ref(false)
  const snippetDialog = ref<{ open: () => Promise<void> } | null>(null)

  const locations = computed(() => props.signature.userLocations.value)
  const locationId = computed(() => props.signature.signatureLocationId.value)
  /** Signatur steht im Text (Knopf „Signatur“ blau). */
  const signatureShown = computed(() => hasSignature(props.html))
  const canSaveTemplate = computed(() => Boolean(plainText(bodyForTemplate(props.html))))

  function editorHtml (): string {
    return props.composer()?.editorEl()?.innerHTML || ''
  }

  /** Neuen Text in den Editor setzen und dem v-model des Composers melden. */
  function setEditorHtml (html: string) {
    const composer = props.composer()
    const el = composer?.editorEl()
    if (!composer || !el) {
      return
    }
    el.innerHTML = html
    composer.syncFromEditor()
    emit('changed')
  }

  // ── Vorlagen ──────────────────────────────────────────────────────

  async function openTemplatePicker () {
    try {
      await props.prepare?.()
    } catch {
      // Falldaten sind nur für Platzhalter – ohne sie bleiben diese markiert.
    }
    suggestedCategory.value = props.suggestCategory?.() || ''
    pickerVisible.value = true
    await templates.load()
  }

  /**
   * Vorlage einfügen: Text oberhalb der Signatur, Platzhalter befüllt; nicht
   * befüllbare bleiben gelb markiert. Den Betreff übernimmt der Container
   * (beim Antworten bleibt der Betreff der Unterhaltung).
   */
  function applyTemplate (tpl: MailTemplate) {
    const rendered = renderTemplateForInsert(tpl, props.templateValues)
    if (rendered.html) {
      setEditorHtml(insertTemplate(editorHtml(), rendered.html))
      focusInsertedTemplate()
    }
    if (rendered.missing > 0) {
      snackbar.showSnackbar({
        message: `${rendered.missing} ${gt('Platzhalter konnte(n) nicht automatisch befüllt werden und sind im Text gelb markiert.')}`,
        type: 'warning',
      })
    }
    emit('template-inserted', rendered)
  }

  /** Cursor auf den ersten offenen Platzhalter bzw. an den Anfang der Vorlage. */
  function focusInsertedTemplate () {
    void nextTick(() => {
      const el = props.composer()?.editorEl()
      const blocks = el?.querySelectorAll('[data-template="true"]')
      // eslint-disable-next-line unicorn/prefer-at -- NodeList kennt .at() nicht (TS-Zielbibliothek)
      const inserted = blocks && blocks.length > 0 ? blocks[blocks.length - 1] : undefined
      const sel = window.getSelection()
      if (!el || !inserted || !sel) {
        return
      }
      const range = document.createRange()
      const missing = inserted.querySelector('.mail-tpl-missing')
      if (missing) {
        range.selectNodeContents(missing)
      } else {
        range.selectNodeContents(inserted)
        range.collapse(true)
      }
      sel.removeAllRanges()
      sel.addRange(range)
      el.focus()
    })
  }

  async function saveAsTemplate () {
    const bodyHtml = bodyForTemplate(editorHtml())
    if (!plainText(bodyHtml)) {
      return
    }
    await templates.load()
    const subject = (props.subject || '').trim()
    editorPrefill.value = {
      name: subject.slice(0, 80) || gt('Neue Vorlage'),
      subject,
      bodyHtml,
      scope: 'personal',
    }
    editorVisible.value = true
  }

  /** Gleiche Rollenprüfung wie der Router-Guard der Kommunikationseinstellungen. */
  function canOpenCommunicationSettings (): boolean {
    const meta = router.resolve({ name: 'PageCommunicationSettings' }).meta || {}
    const store = useAuthStore()
    const userRoles = Array.isArray(meta.allowedUserRoles) ? meta.allowedUserRoles as string[] : null
    const orgRoles = Array.isArray(meta.allowedOrganizationRoles) ? meta.allowedOrganizationRoles as string[] : null
    if (userRoles && store.userRole && !userRoles.includes(store.userRole)) {
      return false
    }
    if (orgRoles && !orgRoles.some(r => (store.organizationRoles || []).includes(r))) {
      return false
    }
    return true
  }

  /**
   * „Vorlagen verwalten“ (Kundenfeedback 25.10.2026 Teil 2, III): direkt in
   * die Vorlagenverwaltung; ohne Zugang als Fenster über dem Text.
   */
  function manageTemplates (link: TemplateManagerLink = {}) {
    pickerVisible.value = false
    if (!canOpenCommunicationSettings()) {
      managerLink.value = link
      managerVisible.value = true
      return
    }
    props.beforeLeave?.()
    templates.invalidate()
    router.push({ name: 'PageCommunicationSettings', query: templateManagerQuery(link) })
      .catch(() => { /* Navigation abgebrochen – Text bleibt erhalten */ })
  }

  // ── Textbausteine ─────────────────────────────────────────────────

  function openSnippets () {
    props.composer()?.saveSelection()
    void snippetDialog.value?.open()
  }

  function insertSnippet (snippet: { content?: string } | null) {
    if (!snippet?.content) {
      return
    }
    props.composer()?.insertHtml(snippet.content)
    emit('changed')
  }

  // ── Signatur ──────────────────────────────────────────────────────

  async function renderedSignature (): Promise<string> {
    await props.signature.ensureSignatureLoaded()
    return props.signature.getRenderedSignature()
  }

  async function toggleSignature () {
    if (signatureBusy.value) {
      return
    }
    const html = editorHtml()
    if (hasSignature(html)) {
      setEditorHtml(removeSignature(html))
      emit('update:signatureOn', false)
      return
    }
    signatureBusy.value = true
    try {
      const sig = await renderedSignature()
      if (!sig) {
        snackbar.showError(gt('Für Sie ist keine Signatur hinterlegt.'))
        return
      }
      setEditorHtml(setSignature(editorHtml(), sig))
      emit('update:signatureOn', true)
    } finally {
      signatureBusy.value = false
    }
  }

  /** Signatur im Text durch die aktuelle ersetzen (Standortwechsel, Bearbeiten). */
  function refreshSignature () {
    if (!hasSignature(editorHtml())) {
      return
    }
    setEditorHtml(setSignature(editorHtml(), props.signature.getRenderedSignature()))
  }

  async function onLocationChange (idLocation: number) {
    if (!idLocation) {
      return
    }
    await props.signature.setSignatureLocation(idLocation)
    refreshSignature()
  }

  async function editSignature () {
    await props.signature.ensureSignatureLoaded()
    signatureEditorVisible.value = true
  }

  async function onSignatureEdited () {
    invalidateCompanySignature(props.signature.signatureLocation()?.idCompany)
    await props.signature.resolveSignatureForLocation()
    refreshSignature()
  }

  // ── Anhänge ───────────────────────────────────────────────────────

  function onFilePick (e: Event) {
    const input = e.target as HTMLInputElement
    const files = Array.from(input.files || [])
    input.value = ''
    if (files.length > 0) {
      emit('attach-files', files)
    }
  }

  defineExpose({ openTemplatePicker })
</script>

<style scoped>
.ct {
  /* Beschriftungen blenden sich in schmalen Fenstern aus (Tooltip bleibt). */
  container-type: inline-size;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 10px;
}
.ct__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.ct__end {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}
.ct__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 9px;
  border: 1px solid var(--border-grey-color);
  border-radius: 8px;
  background: #fff;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  color: var(--grey-color);
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.ct__btn:hover:not(:disabled) { background: var(--grey-light-color); color: var(--dark-color); }
.ct__btn:focus-visible,
.ct__btn:focus-within { outline: 2px solid var(--primary-color); outline-offset: 1px; }
.ct__btn:disabled { opacity: 0.45; cursor: not-allowed; }
/* Ausgewählt: blau mit gut lesbarer Schrift (Kundenvorgabe). */
.ct__btn--on,
.ct__btn--on:hover:not(:disabled) {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: #fff;
}
.ct__btn--select { padding-right: 4px; }
.ct__select {
  max-width: 180px;
  border: none;
  background: transparent;
  font-size: 12px;
  color: inherit;
  cursor: pointer;
  outline: none;
}
.ct__select option { color: var(--dark-color); }

/* Feste Höhe: Unternehmens- und eigene Vorlagen im gleich großen Fenster
   (Kundenfeedback 26.10.2026, VI). */
.ct__manager { display: flex; flex-direction: column; height: min(90vh, 820px); }
.ct__manager-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}
.ct__manager-title { margin: 0; font-size: 1rem; font-weight: 600; }
.ct__manager-body { flex: 1; min-height: 0; padding: 12px 16px 16px; overflow: auto; }

@container (max-width: 760px) {
  .ct__label { display: none; }
}
</style>
